/**
 * Button component for attaching files to messages
 */

import { useRef, useCallback, type ChangeEvent } from 'react';
import type { Attachment } from '../../types';
import {
  MAX_FILE_SIZE_BYTES,
  type AttachmentResult,
  getAcceptStringForContext,
  processAttachmentFiles,
} from '../../utils/attachment';
import './AttachmentButton.css';

export { MAX_FILE_SIZE_BYTES } from '../../utils/attachment';

interface AttachmentButtonProps {
  /** Handler called when files are selected */
  onAttach: (attachments: Attachment[]) => void;
  /** Optional handler called with full result including rejected files */
  onAttachResult?: (result: AttachmentResult) => void;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Maximum file size in bytes (default: 10 MB) */
  maxFileSize?: number;
  /** Whether code interpreter is enabled (affects supported file types) */
  codeInterpreterEnabled?: boolean;
}

/**
 * Button that opens a file picker for attaching images or PDFs
 */
export function AttachmentButton({
  onAttach,
  onAttachResult,
  disabled = false,
  maxFileSize = MAX_FILE_SIZE_BYTES,
  codeInterpreterEnabled = false,
}: AttachmentButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;

      let result: AttachmentResult = { attachments: [], rejectedFiles: [] };

      try {
        result = await processAttachmentFiles(Array.from(files), {
          maxFileSize,
          codeInterpreterEnabled,
        });
        if (result.attachments.length > 0) {
          onAttach(result.attachments);
        }
      } catch (error) {
        console.error('Failed to process attachments:', error);
      }

      // Notify about complete result including rejections
      if (onAttachResult) {
        onAttachResult(result);
      }

      // Reset input to allow selecting the same file again
      e.target.value = '';
    },
    [onAttach, onAttachResult, maxFileSize, codeInterpreterEnabled]
  );

  // Generate title/tooltip based on context
  const buttonTitle = codeInterpreterEnabled
    ? 'Attach files (images, PDF, CSV, JSON, Excel, Word, code)'
    : 'Attach files (images, PDF)';

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={getAcceptStringForContext(codeInterpreterEnabled)}
        multiple
        onChange={handleChange}
        className="attachment-button__input"
        aria-hidden="true"
        tabIndex={-1}
      />
      <button
        type="button"
        className="attachment-button"
        onClick={handleClick}
        disabled={disabled}
        aria-label="Attach file"
        title={buttonTitle}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
        </svg>
      </button>
    </>
  );
}
